import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Clock, 
  X, 
  Sun, 
  Moon, 
  Calendar,
  Loader2,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/client';
import { cn } from '../../utils/cn';

interface SnoozePreset {
  key: string;
  label: string;
  time: string;
}

interface SnoozeModalProps {
  accountId: string;
  messageId: string;
  onClose: () => void;
  onSnoozed?: () => void;
}

const PRESET_ICONS: Record<string, React.ReactNode> = {
  '1_hour': <Clock className="w-4 h-4" />,
  '3_hours': <Clock className="w-4 h-4" />,
  'later_today': <Sun className="w-4 h-4" />,
  'tomorrow_morning': <Sun className="w-4 h-4" />,
  'tomorrow_afternoon': <Sun className="w-4 h-4" />,
  'next_week': <Calendar className="w-4 h-4" />,
  'next_month': <Calendar className="w-4 h-4" />,
};

export default function SnoozeModal({ accountId, messageId, onClose, onSnoozed }: SnoozeModalProps) {
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');
  const [snoozeReason, setSnoozeReason] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const queryClient = useQueryClient();
  
  // Fetch presets
  const { data: presetsData, isLoading: presetsLoading } = useQuery({
    queryKey: ['snoozePresets'],
    queryFn: async () => {
      const response = await api.get('mail/snooze/presets').json<{ presets: SnoozePreset[] }>();
      return response.presets;
    },
    staleTime: 5 * 60 * 1000,
  });
  
  const presets = presetsData || [];
  
  // Snooze mutation
  const snoozeMutation = useMutation({
    mutationFn: async (params: { preset?: string; snoozeUntil?: string }) => {
      // Use query param for messageId to handle MS Graph IDs with special chars
      return api.post(`mail/${accountId}/message/snooze`, {
        searchParams: { id: messageId },
        json: {
          ...params,
          snoozeReason: snoozeReason || undefined,
        },
      }).json();
    },
    onSuccess: () => {
      toast.success('E-Mail wird zurückgestellt');
      queryClient.invalidateQueries({ queryKey: ['snoozeStatus', accountId, messageId] });
      queryClient.invalidateQueries({ queryKey: ['userSnoozes'] });
      onSnoozed?.();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.message || 'Snooze fehlgeschlagen');
    },
  });
  
  const handlePresetSnooze = (presetKey: string) => {
    snoozeMutation.mutate({ preset: presetKey });
  };
  
  const handleCustomSnooze = () => {
    if (!customDate) {
      toast.error('Bitte Datum auswählen');
      return;
    }
    
    const datetime = new Date(`${customDate}T${customTime}:00`);
    
    if (datetime <= new Date()) {
      toast.error('Zeitpunkt muss in der Zukunft liegen');
      return;
    }
    
    snoozeMutation.mutate({ snoozeUntil: datetime.toISOString() });
  };
  
  const formatPresetTime = (isoTime: string) => {
    const date = new Date(isoTime);
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    
    const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    
    if (isToday) {
      return `Heute, ${timeStr}`;
    }
    if (isTomorrow) {
      return `Morgen, ${timeStr}`;
    }
    
    return date.toLocaleDateString('de-DE', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  const isLoading = snoozeMutation.isPending;
  
  // Get minimum date (today)
  const today = new Date().toISOString().split('T')[0];
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden animate-in slide-in-from-bottom duration-300"
        style={{ backgroundColor: 'var(--bg-primary)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-5 py-4"
          style={{ 
            borderBottom: '1px solid var(--border-default)',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(249, 115, 22, 0.1))',
          }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(135deg, #f59e0b, #f97316)',
              }}
            >
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                Zurückstellen
              </h2>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Wann soll die E-Mail wieder auftauchen?
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-5 max-h-[60vh] overflow-y-auto">
          {presetsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
            </div>
          ) : (
            <>
              {/* Presets */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    onClick={() => handlePresetSnooze(preset.key)}
                    disabled={isLoading}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-xl text-left transition-all disabled:opacity-50',
                      'hover:ring-2 hover:ring-offset-2'
                    )}
                    style={{
                      backgroundColor: 'var(--bg-secondary)',
                      border: '1px solid var(--border-default)',
                      '--tw-ring-color': '#f59e0b',
                      '--tw-ring-offset-color': 'var(--bg-primary)',
                    } as any}
                  >
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ 
                        backgroundColor: 'rgba(245, 158, 11, 0.15)',
                        color: '#f59e0b',
                      }}
                    >
                      {PRESET_ICONS[preset.key] || <Clock className="w-4 h-4" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                        {preset.label}
                      </p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                        {formatPresetTime(preset.time)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Custom Time Toggle */}
              <button
                onClick={() => setShowCustom(!showCustom)}
                className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium transition-colors"
                style={{ color: 'var(--brand-primary)' }}
              >
                <Calendar className="w-4 h-4" />
                {showCustom ? 'Presets anzeigen' : 'Eigenes Datum wählen'}
              </button>
              
              {/* Custom Date/Time */}
              {showCustom && (
                <div 
                  className="mt-4 p-4 rounded-xl space-y-3"
                  style={{ 
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-default)',
                  }}
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Datum
                      </label>
                      <input
                        type="date"
                        value={customDate}
                        onChange={e => setCustomDate(e.target.value)}
                        min={today}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                        style={{
                          backgroundColor: 'var(--bg-primary)',
                          border: '1px solid var(--border-default)',
                          color: 'var(--text-primary)',
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                        Uhrzeit
                      </label>
                      <input
                        type="time"
                        value={customTime}
                        onChange={e => setCustomTime(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg text-sm"
                        style={{
                          backgroundColor: 'var(--bg-primary)',
                          border: '1px solid var(--border-default)',
                          color: 'var(--text-primary)',
                        }}
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={handleCustomSnooze}
                    disabled={isLoading || !customDate}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white disabled:opacity-50"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Zurückstellen
                  </button>
                </div>
              )}
              
              {/* Optional Reason */}
              <div className="mt-4">
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
                  Notiz (optional)
                </label>
                <input
                  type="text"
                  value={snoozeReason}
                  onChange={e => setSnoozeReason(e.target.value)}
                  placeholder="z.B. 'Nach dem Meeting prüfen'"
                  maxLength={200}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>
            </>
          )}
        </div>
        
        {/* Mobile Cancel */}
        <div 
          className="p-4 sm:hidden"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          <button
            onClick={onClose}
            className="w-full py-3 rounded-lg font-medium"
            style={{
              backgroundColor: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
            }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

// Snooze Button for inline use
export function SnoozeButton({ 
  onClick,
  size = 'default',
  snoozedUntil,
}: { 
  onClick: () => void;
  size?: 'small' | 'default';
  snoozedUntil?: Date;
}) {
  const isSnoozed = snoozedUntil && new Date(snoozedUntil) > new Date();
  
  return (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 font-medium rounded-lg transition-all',
        size === 'small' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm'
      )}
      style={{
        backgroundColor: isSnoozed ? 'rgba(245, 158, 11, 0.15)' : 'var(--bg-secondary)',
        border: `1px solid ${isSnoozed ? 'rgba(245, 158, 11, 0.3)' : 'var(--border-default)'}`,
        color: isSnoozed ? '#f59e0b' : 'var(--text-secondary)',
      }}
      title={isSnoozed ? `Zurückgestellt bis ${new Date(snoozedUntil).toLocaleString('de-DE')}` : 'Zurückstellen'}
    >
      <Clock className={size === 'small' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
      {isSnoozed ? 'Snoozed' : 'Snooze'}
    </button>
  );
}
